/**
 * Hook para gerenciar o salvamento do arquivo no Corel + API
 */

import { useState, useCallback } from 'react';
import { useNotifications } from '../../../feedback/NotificationProvider';
import { buildPreOrcamentoDir, generatePreOrcFileName, slugifyName } from '../../../../utils/pathTemplates';
import { sendToTerminal } from '../../../../lib/terminalLogger';
import { NovoArquivoSubmitData } from '../types';
import { runGerArquivosSavePipeline } from '../../../../lib/corel/pipeline/gerArquivosSave';
import { buscarRegraPorCategoria } from '../../../../utils/fileNaming';
import { resolveFilePath, type PreOrcamentoPayload } from '../../../../services/pathResolverService';
import { requestConfirmation } from '../../../../lib/globalConfirm';

interface SalvamentoParams {
  submitData: NovoArquivoSubmitData;
  digits: string;
  nomeOpcional?: string;
  userId: number;
  userName: string;
  apiMode?: 'simple' | 'advanced';
  onProgress?: (event: { stage: string; meta?: Record<string, unknown> }) => void;
}

export function useArquivoSalvamento() {
  const [salvando, setSalvando] = useState(false);
  const [progress, setProgress] = useState<{ stage: string; meta?: Record<string, unknown> } | null>(null);
  const { notify } = useNotifications();

  /**
   * Salva o arquivo no Corel e confirma na API
   */
  const salvar = useCallback(async (params: SalvamentoParams): Promise<{ success: boolean; error?: string }> => {
    const { submitData, digits, nomeOpcional, userId, userName, apiMode = 'simple', onProgress } = params;

    setSalvando(true);
    setProgress({ stage: 'start' });
    
    try {
      try {
        sendToTerminal('GERARQ:SAVE_FLOW_START', {
          cliente_id: submitData.cliente?.id,
          cli_whatsapp: submitData.cli_whatsapp ? '***' : undefined,
          prod_cat_item: submitData.prod_cat_item
        });
      } catch { /* ignore */ }

      const hasCadastro = !!submitData.cliente;
      const nomeSimplificado = hasCadastro ? undefined : slugifyName(nomeOpcional || '');

      if (!submitData.prod_cat_item) {
        throw new Error('ID da categoria (prod_cat_item) é obrigatório para buscar diretório base');
      }

      // Tentar resolver via backend PathResolver com retry + fallback local
      let diretorio: string;
      let nomeArquivo: string;

      try {
        const payload: PreOrcamentoPayload = {
          flow: 'pre_orcamento',
          userId,
          cliWhatsapp: digits,
          hasCadastro,
          nomeOpcional: nomeOpcional || undefined,
          clienteId: submitData.cliente?.id,
          clienteNomeFantasia: submitData.cliente?.empresa || submitData.cliente?.nome,
          categoriaId: submitData.prod_cat_item,
        };
        const resolved = await resolveFilePath(payload);
        diretorio = resolved.smb.directory;
        nomeArquivo = resolved.smb.fileName;
        sendToTerminal('✅ [GERARQ] Path resolvido via backend', {
          diretorio,
          nomeArquivo,
          storageMethod: resolved.metadata.storageMethod,
        });
      } catch (backendError) {
        const errorMsg = backendError instanceof Error ? backendError.message : String(backendError);
        sendToTerminal('⚠️ [GERARQ] Backend indisponível após retries', { error: errorMsg });

        const useFallback = await requestConfirmation({
          title: 'Backend indisponível',
          message: `Não foi possível resolver o caminho via servidor.\n\nDeseja usar o cálculo local como alternativa?\n\n(Erro: ${errorMsg})`,
          confirmLabel: 'Usar cálculo local',
          cancelLabel: 'Cancelar',
        });

        if (!useFallback) {
          return { success: false, error: 'Salvamento cancelado — backend indisponível.' };
        }

        sendToTerminal('🔄 [GERARQ] Usuário aceitou fallback local');

        // Fallback: lógica local original
        const regra = await buscarRegraPorCategoria(submitData.prod_cat_item);
        const diretorioBaseGerenciar = regra.diretorio_base_gerenciar;

        if (!diretorioBaseGerenciar || diretorioBaseGerenciar.trim() === '') {
          throw new Error(`diretorio_base_gerenciar não encontrado ou vazio para categoria ID: ${submitData.prod_cat_item}`);
        }

        diretorio = buildPreOrcamentoDir({
          userId,
          userName,
          cliWhatsapp: digits,
          hasCadastro,
          nomeSimplificado,
          clienteId: submitData.cliente?.id,
          clienteNomeFantasia: submitData.cliente?.empresa || submitData.cliente?.nome,
          categoriaId: submitData.prod_cat_item,
          baseDir: diretorioBaseGerenciar,
        });

        nomeArquivo = generatePreOrcFileName(
          digits,
          userId,
          new Date(),
          nomeSimplificado,
          submitData.cliente?.empresa || submitData.cliente?.nome,
          hasCadastro,
        );
      }

      try {
        sendToTerminal('GERARQ:SAVE_PREPARE', { diretorio, nomeArquivo });
      } catch { /* ignore */ }

      // Executar pipeline centralizada simplificada
      const pipeRes = await runGerArquivosSavePipeline({
        nomeArquivo,
        diretorio,
        prod_cat_item: submitData.prod_cat_item,
        observacoes: submitData.observacoes || '',
        cliente_id: submitData.cliente?.id,
        cli_whatsapp: submitData.cli_whatsapp,
        apiMode,
        onProgress: (ev) => {
          setProgress(ev);
          try { sendToTerminal('GERARQ:PIPE_PROGRESS', { stage: ev.stage, meta: ev.meta }); } catch {}
          try { onProgress?.(ev); } catch {}
          
          // Tratar cancelamento/erro de forma centralizada
          // Quando houver erro do C#, API ou cancelamento, limpar progress após delay
          if (ev.stage === 'save:error' || ev.stage === 'error' || ev.stage === 'api:error' || ev.stage === 'ui:cancel') {
            // Aguardar 2 segundos para o usuário ver a mensagem de erro antes de limpar
            setTimeout(() => {
              setProgress(null);
            }, 2000);
          }
        }
      });
      if (pipeRes.status !== 'success' || !pipeRes?.data?.caminhoSalvo) {
        throw new Error(pipeRes?.error?.message || 'Falha no pipeline de salvamento');
      }
      const caminhoSalvo = pipeRes.data.caminhoSalvo;
      try { notify({ type: 'success', message: `Salvo em: ${caminhoSalvo}` }); } catch {}

      try {
        sendToTerminal('GERARQ:SAVE_DONE', { caminhoSalvo });
      } catch { /* ignore */ }

      setProgress({ stage: 'done' });
      
      // Delay de 2 segundos para mostrar feedback de sucesso
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return { success: true };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro ao salvar arquivo';
      
      try {
        sendToTerminal('GERARQ:SAVE_FLOW_ERROR', { error: errorMsg });
      } catch { /* ignore */ }

      // Limpar progress em caso de erro para garantir que a UI seja resetada
      setProgress(null);
      
      return { success: false, error: errorMsg };
    } finally {
      setSalvando(false);
    }
  }, [notify]);

  return {
    salvando,
    salvar,
    progress
  };
}

